/**
 * The route PATTERN a pathname matched, for `pageViewed` and `clientErrorRaised`.
 *
 * Analytics must never carry a workspace slug, a dashboard slug or a resource id, so the emitted
 * `route` is always a pattern from this list — never the pathname.
 *
 * `PATTERNS` mirrors the route tree in `App.tsx`. It is not maintained by hand: `route-pattern.test.ts`
 * derives the real tree from `AppRoutes` and fails when the two disagree, so a route added there cannot
 * silently fall into one bucket here.
 */
import { matchRoutes } from 'react-router-dom';

export const PATTERNS = [
  '/',
  '/*',
  '/chat/:id',
  '/chats',
  '/context/*',
  '/d/:slug',
  '/dashboards',
  '/forgot-password',
  '/invite/:inviteId',
  '/login',
  '/login/callback',
  '/new-workspace',
  '/oauth/rudderstack',
  '/pub/:token',
  '/reset-password',
  '/rudderstack-invite',
  '/settings/*',
  '/signup',
  '/signup/complete',
  '/w/:slug',
  '/w/:slug/*',
  '/w/:slug/activations',
  '/w/:slug/activations/:sourceId/:syncId',
  '/w/:slug/audiences',
  '/w/:slug/audiences/:id',
  '/w/:slug/chat/:id',
  '/w/:slug/chats',
  '/w/:slug/context',
  '/w/:slug/context/:kind/:id',
  '/w/:slug/context/generated/:id',
  '/w/:slug/context/review',
  '/w/:slug/context/skill/:id',
  '/w/:slug/d/:dashSlug',
  '/w/:slug/dashboards',
  '/w/:slug/docs',
  '/w/:slug/docs/:id',
  '/w/:slug/settings',
  '/w/:slug/settings/*',
  '/w/:slug/settings/accounts',
  '/w/:slug/settings/context',
  '/w/:slug/settings/integrations',
  '/w/:slug/settings/integrations/:section',
  '/w/:slug/settings/usage',
  '/w/:slug/settings/users',
  '/w/:slug/settings/warehouses',
  '/w/:slug/workflows',
  '/w/:slug/workflows/:id',
  '/w/:slug/workflows/new',
  '/workflows',
  '/workflows/:id',
] as const;

const ROUTE_OBJECTS = PATTERNS.map((path) => ({ path }));

/**
 * Workspace sections — the first segment after the slug. Derived from `PATTERNS` rather than listed,
 * so it inherits that list's drift test instead of needing its own.
 */
const SECTIONS: ReadonlySet<string> = new Set(
  PATTERNS.filter((pattern) => pattern.startsWith('/w/:slug/'))
    .map((pattern) => pattern.split('/')[3])
    .filter((segment): segment is string => segment !== undefined && segment !== '*'),
);

/** The matched pattern, or `unknown` when nothing matched — which the catch-alls make rare. */
export function routePatternOf(pathname: string): string {
  const matches = matchRoutes(ROUTE_OBJECTS, pathname);
  return matches?.[matches.length - 1]?.route.path ?? 'unknown';
}

/** `chat`, `dashboards`, `settings` and so on. `workspace` is the workspace root, `public` is outside one. */
export function routeSectionOf(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] !== 'w') return 'public';
  const section = segments[2];
  if (section === undefined) return 'workspace';
  return SECTIONS.has(section) ? section : 'unknown';
}
